/**
 * API client for distribution + distribution_project_platforms (SQL).
 * Base URL from getBaseUrl() (REACT_APP_DISTRIBUTION_API_URL_DEV / REACT_APP_DISTRIBUTION_API_URL_PROD).
 * Expects backend to expose:
 *   GET /distribution?project_id=<id>
 * Returns: { distribution: { user_id, project_id, created_at, title, status } | null, platforms: Array }
 */

import { getBaseUrl } from "./baseUrl";
import { authFetch } from "./apiFetch";

function delay(ms) {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

/** HTTP statuses where a short retry often succeeds (cold DB, pool checkout, transient proxy errors). */
function isTransientHttpStatus(status) {
  if (status === 408 || status === 429) return true;
  return status >= 500 && status <= 599;
}

/** @returns {Promise<Array<{ platform_id: string, display_name: string, chart_color: string | null, ... }>>} */
export async function getPlatforms() {
  const base = getBaseUrl();
  if (!base) return [];

  const url = `${base}/platforms`;
  try {
    const res = await authFetch(url);
    if (!res.ok) {
      console.error("platforms API error", res.status, await res.text());
      return [];
    }
    const data = await res.json();
    return data.platforms || [];
  } catch (err) {
    console.error("Failed to fetch platforms catalog", err);
    return [];
  }
}

/**
 * Fetch distribution project and its platforms from SQL.
 * @param {string} projectId - Distribution project id (project_id).
 * @returns {Promise<{ distribution: object | null, platforms: Array }>}
 */
export async function getDistributionProject(projectId) {
  const base = getBaseUrl();
  if (!base || !projectId) {
    return { distribution: null, platforms: [] };
  }

  const params = new URLSearchParams({ project_id: projectId });
  const url = `${base}/distribution?${params.toString()}`;
  if (process.env.NODE_ENV === "development") {
    console.log("[distribution] fetching project info:", url);
  }
  try {
    const res = await authFetch(url);
    if (!res.ok) {
      console.error("distribution API error", res.status, await res.text());
      return { distribution: null, platforms: [] };
    }
    const data = await res.json();
    return {
      distribution: data.distribution || null,
      platforms: data.platforms || [],
    };
  } catch (err) {
    console.error("Failed to fetch distribution project", err);
    return { distribution: null, platforms: [] };
  }
}

/**
 * Fetch platforms for a single project (decoupled from distribution/transactions). For project page Platforms collapse.
 * @param {string} projectId - Distribution project id (project_id).
 * @returns {Promise<Array<{ project_id, platform_id, status, link, msg, ... }>>}
 */
export async function getDistributionProjectPlatforms(projectId) {
  const base = getBaseUrl();
  if (!base || !projectId) return [];

  const params = new URLSearchParams({ project_id: projectId });
  const url = `${base}/distribution-project-platforms?${params.toString()}`;
  if (process.env.NODE_ENV === "development") {
    console.log("[distribution-project-platforms] fetching:", url);
  }
  try {
    const res = await authFetch(url);
    if (!res.ok) {
      console.error(
        "distribution-project-platforms API error",
        res.status,
        await res.text()
      );
      return [];
    }
    const data = await res.json();
    return data.platforms || [];
  } catch (err) {
    console.error("Failed to fetch distribution project platforms", err);
    return [];
  }
}

/**
 * Fetch all distributions for a user from SQL.
 * @param {string} userId - user_id.
 * @returns {Promise<Array<{ project_id, title, status, ... }>>}
 */
export async function getDistributionsByUser(userId) {
  const base = getBaseUrl();
  if (!base || !userId) return [];

  const params = new URLSearchParams({ user_id: userId });
  const url = `${base}/distributions?${params.toString()}`;
  try {
    const res = await authFetch(url);
    if (!res.ok) return [];
    const data = await res.json();
    return data.distributions || [];
  } catch (err) {
    console.error("Failed to fetch distributions by user", err);
    return [];
  }
}

/**
 * Fetch sales summary + project list for dashboard (one call).
 * @param {string} userId - user_id
 * @returns {Promise<{ total_revenue: number, distributions: Array }>}
 */
export async function getDistributionSalesSummary(userId) {
  const base = getBaseUrl();
  if (!base || !userId) {
    return { total_revenue: 0, distributions: [] };
  }

  const params = new URLSearchParams({ user_id: userId });
  const url = `${base}/distribution-sales-summary?${params.toString()}`;
  try {
    const res = await authFetch(url);
    if (!res.ok) return { total_revenue: 0, distributions: [] };
    const data = await res.json();
    return {
      total_revenue: Number(data.total_revenue) || 0,
      distributions: data.distributions || [],
    };
  } catch (err) {
    console.error("Failed to fetch distribution sales summary", err);
    return { total_revenue: 0, distributions: [] };
  }
}

/**
 * Fetch payout summary aggregated by (project, platform, status).
 * @param {string} userId - user_id
 * @returns {Promise<{ payouts: Array<{ project_id, project_title, platform_id, status, period_label, total_revenue }> }>}
 */
export async function getDistributionPayouts(userId) {
  const base = getBaseUrl();
  if (!base || !userId) return { payouts: [] };

  const params = new URLSearchParams({ user_id: userId });
  const url = `${base}/distribution-payouts?${params.toString()}`;
  try {
    const res = await authFetch(url);
    if (!res.ok) return { payouts: [] };
    const data = await res.json();
    return { payouts: data.payouts || [] };
  } catch (err) {
    console.error("Failed to fetch distribution payouts", err);
    return { payouts: [] };
  }
}

/**
 * Fetch payout detail (raw line items) with server-side pagination and filters.
 * @param {string} userId - user_id
 * @param {number} page - 1-based
 * @param {number} limit - page size (default 20)
 * @param {Object} filters - optional { project_title, platform_id, status }
 * @returns {Promise<{ payouts: Array, total_count: number, error?: string }>}
 */
export async function getDistributionPayoutsDetail(
  userId,
  page = 1,
  limit = 20,
  filters = {}
) {
  const base = getBaseUrl();
  if (!base || !userId) return { payouts: [], total_count: 0 };

  const params = new URLSearchParams({
    user_id: userId,
    page: String(page),
    limit: String(limit),
  });
  if (filters.project_id) {
    params.set("project_id", filters.project_id);
  }
  if (filters.project_title) {
    params.set("project_title", filters.project_title);
  }
  if (filters.platform_id) {
    params.set("platform_id", filters.platform_id);
  }
  if (filters.status != null && filters.status !== "") {
    params.set("status", String(filters.status));
  }
  const url = `${base}/distribution-payouts-detail?${params.toString()}`;

  const maxAttempts = 4;
  const baseDelayMs = 400;

  for (let attempt = 0; attempt < maxAttempts; attempt++) {
    try {
      const res = await authFetch(url);
      if (res.ok) {
        const data = await res.json();
        return {
          payouts: data.payouts || [],
          total_count: Number(data.total_count) || 0,
        };
      }

      let body = null;
      try {
        body = await res.json();
      } catch {
        /* non-JSON error body */
      }
      const message =
        typeof body?.error === "string"
          ? body.error
          : `Request failed (${res.status})`;

      if (isTransientHttpStatus(res.status) && attempt < maxAttempts - 1) {
        await delay(baseDelayMs * 2 ** attempt);
        continue;
      }

      return { payouts: [], total_count: 0, error: message };
    } catch (err) {
      console.error("Failed to fetch distribution payouts detail", err);
      if (attempt < maxAttempts - 1) {
        await delay(baseDelayMs * 2 ** attempt);
        continue;
      }
      return { payouts: [], total_count: 0, error: "Network error" };
    }
  }

  return { payouts: [], total_count: 0, error: "Request failed" };
}

/**
 * Fetch filter options for payout detail page: platforms and projects for this user.
 * @param {string} userId - user_id
 * @returns {Promise<{ platforms: Array<{ platform_id, platform_name }>, projects: Array<{ project_id, title }> }>}
 */
export async function getDistributionPayoutFilters(userId) {
  const base = getBaseUrl();
  if (!base || !userId) return { platforms: [], projects: [] };

  const params = new URLSearchParams({ user_id: userId });
  const url = `${base}/distribution-payout-filters?${params.toString()}`;
  try {
    const res = await authFetch(url);
    if (!res.ok) return { platforms: [], projects: [] };
    const data = await res.json();
    return {
      platforms: data.platforms || [],
      projects: data.projects || [],
    };
  } catch (err) {
    console.error("Failed to fetch distribution payout filters", err);
    return { platforms: [], projects: [] };
  }
}
